import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import {
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
} from "@mui/material";
import { DeleteForever, DeleteRounded, Search } from "@mui/icons-material";
import { chunk, escapeRegExp } from "lodash";
import { ReactComponent as FilterSvg } from "assets/Svgs/filter.svg";

import { ReactComponent as DeleteSvg } from "assets/Svgs/delete.svg";
import { ReactComponent as EditSvg } from "assets/Svgs/RoundEdit.svg";
import { ReactComponent as ActiveSvg } from "assets/Svgs/EllipseSvg.svg";
import { ReactComponent as InActiveSvg } from "assets/Svgs/EllipseGraySvg.svg";

import { useBoundStore } from "store/store";
import moment from "moment";
import i18n from "i18n/i18n";
import { orderBy as odb } from "lodash";
import { getHighlightedText } from "utils/helper";
import { Link, useNavigate } from "react-router-dom";
// import UserCard from "./UserCard";
import DeletePopUp from "components/DeletePopup";

interface Data {
  s: any;
  id: number;
  Vorname: string;
  Nachname: string;
  Benutzertyp: string;
  EMailAdresse: string;
  Actions: any;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order | any,
  orderBy: Key | any
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  lang: string;
  activeId?: any;
  setActiveId: any;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const headCells: readonly HeadCell[] = [
    {
      id: "s",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang)?.user_table_s,
    },
    {
      id: "Vorname",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang)?.user_table_Vorname,
    },
    {
      id: "Nachname",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang).user_table_Nachname,
    },
    {
      id: "Benutzertyp",
      numeric: false,
      disablePadding: true,
      label: i18n(props.lang).user_table_Benutzertyp,
    },
    {
      id: "EMailAdresse",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang).user_table_email,
    },
    {
      id: "Actions",
      numeric: false,
      disablePadding: false,
      label: "Actions",
    },
  ];

  const { order, orderBy, onRequestSort, setActiveId } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
      setActiveId(property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            className="tableHead"
            width={index === 0 ? "5%" : index === 5 ? "5%" : "20%"}
            id={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{ display: "flex", flexDirection: "row" }}
              IconComponent={FilterSvg}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const UsersTable: React.FC<any> = ({
  searchText,
  data,
  handlePageChange,
  page,
  setPage,
  viewMode,
}) => {
  const loadTopology: any = useBoundStore((state) => state.loadTopology);

  const lang: any = useBoundStore((state) => state.lang);


  const deleteUser: any = useBoundStore((state) => state.deleteUser);

  const [userNodeId, setUserNodeId] = React.useState(null);

  const isAuthUser = useBoundStore((state) => state.isAuthUser);

  const emptySites: any = useBoundStore((state) => state.emptySites);

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("id");
  const [selected, setSelected] = React.useState<readonly number[]>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [activeId, setActiveId] = React.useState("Vorname");

  const [openDialogDelete, setopenDialogDelete] = React.useState(false);

  const getAllUsers = useBoundStore((store) => store.getAllUsers);
  const getAllAclUsers = useBoundStore((store) => store.getAllAclUsers);
  const legalEntityNodeId = useBoundStore((store) => store.legalEntityNodeId);

  const navigate = useNavigate();

  const handleDialogDelete = () => {
    setopenDialogDelete(false);
    setUserNodeId(null);
  };

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    let sortedArray: any = [];


    switch (activeId) {
      case "Vorname":
        sortedArray = odb(array, ["Vorname"], [order]);
        break;
      case "Nachname":
        sortedArray = odb(array, ["Nachname"], [order]);
        break;
      case "Benutzertyp":
        sortedArray = odb(array, ["Benutzertyp"], [order]);
        break;
      case "EMailAdresse":
        sortedArray = odb(array, ["EMailAdresse"], [order]);
        break;
      default:
        sortedArray = odb(array, ["date"], [order]);
    }

    return sortedArray;
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };


  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;



  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    (window as any).blurAll();
  };

  let paginationchunks = chunk(data, rowsPerPage);

  const handleDelete = async () => {
    const promises: any[] = [deleteUser(userNodeId)]; // Ensure that promises is an array

    if (isAuthUser === true) {
      try {
        let lc: any = localStorage.getItem("local-storage");
        lc = JSON.parse(lc);

        const result = await Promise.all(promises);

        setopenDialogDelete(false);
        setTimeout(() => {
          getAllUsers(legalEntityNodeId, lc.state.user.nodeid);
          getAllAclUsers();
        }, 2000);
      } catch (error) {
        console.error("Error during Promise.all:", error);
        // Handle the error appropriately
      }
    }
  };

  return (
    <>
      {viewMode === "table" ? (
        <div>
          <Table
            aria-labelledby="tableTitle"
            size="medium"
            className="dashboardTable usersTable"
            sx={{ marginTop: "3%" }}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              lang={lang}
              setActiveId={setActiveId}
              activeId={activeId}
            />

            <TableBody>
              {data.length > 0 ? (
                stableSort(data, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any, index: any) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    
                    return (
                      <TableRow
                        key={index}
                        style={{
                          height:
                            document.getElementById("rowDes-" + (index + 1))
                              ?.offsetHeight + "px",
                        }}
                        
                      >
                        <TableCell
                          align="right"
                          className={
                            typeof row.s === "string" &&
                            row.s?.toLowerCase().includes("error")
                              ? "errorText"
                              : ""
                          }
                          sx={{ paddingBottom: "0px"  }}
                        >
                          <div className="rowTitle">
                            {row.user.active ? <ActiveSvg /> : <InActiveSvg />}
                          </div>
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          style={{
                            position: "relative",
                            paddingTop: "10px",
                            paddingBottom: "5px",
                            
                          }}
                          id={labelId}
                        >
                          <div className="rowTitle" style={{ color: row.user.active ? "#000000DE" : "#00000061"}}>
                            {getHighlightedText(row.user.givenname, searchText)}
                          </div>
                        </TableCell>
                        <TableCell
                          align="right"
                          className={
                            typeof row.s === "string" &&
                            row.s?.toLowerCase().includes("error")
                              ? "errorText"
                              : ""
                          }
                        >
                          <div className="rowTitle" style={{ color: row.user.active ? "#000000DE" : "#00000061"}}>
                            {getHighlightedText(row.user.surname, searchText)}
                          </div>
                        </TableCell>
                        <TableCell
                          align="right"
                          className={
                            typeof row.s === "string" &&
                            row.s?.toLowerCase().includes("error")
                              ? "errorText"
                              : ""
                          }
                        >
                          <div className="rowTitle" style={{ color: row.user.active ? "#000000DE" : "#00000061"}}>
                            {row.userType}
                          </div>
                        </TableCell>

                        <TableCell align="right">
                          <div className="rowTitle" style={{ color: row.user.active ? "#000000DE" : "#00000061"}}>
                            {getHighlightedText(row.user.email, searchText)}
                          </div>
                        </TableCell>

                        <TableCell
                          align="right"
                          className={
                            index === data.length - 1 ? "lastTableRowCell" : ""
                          }
                        >
                          <div
                            className="flex"
                            style={{
                              alignItems: "center",
                              justifyContent: "right",
                            }}
                          >
                            <div
                              onClick={() => {
                                setopenDialogDelete(true);
                                setUserNodeId(row.user.nodeid);
                              }}
                              className="cp"
                            >
                              <DeleteSvg />
                            </div>
                            <Link
                              className="cp"
                              state={{ pageType: "edit", user: {...row.user, userType:row.userType} }}
                              to={"/add-user"}
                            >
                              <EditSvg height={30} width={20} />
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {i18n(lang)?.no_data}
                  </TableCell>
                </TableRow>
              )}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={5} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {data.length ? (
            data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user: any, index: any) => {
                return (
                  <>
                    <Grid item xs={6} md={6} key={index}>
                      {/* <UserCard user={user} /> */}
                    </Grid>
                  </>
                );
              })
          ) : (
            <div></div>
          )}
        </Grid>
      )}

      <div className="DashboardTablePagination">
        <div>
          {paginationchunks.length > 0 ? (
            <Pagination
              count={paginationchunks.length}
              shape="rounded"
              onChange={handlePageChange}
              hideNextButton={page === paginationchunks.length - 1}
              hidePrevButton={page === 0}
            />
          ) : (
            ""
          )}
        </div>
        <div>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              label={i18n(lang)?.rows_per_page}
              className="perPageSelect"
            >
              <MenuItem value={5}>
                {5}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={5 * 2}>
                {5 * 2}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={5 * 5}>
                {5 * 5}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={5 * 10}>
                {5 * 10}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <DeletePopUp
        open={openDialogDelete}
        handleClose={handleDialogDelete}
        handleDelete={handleDelete}
        topic="Users"
      ></DeletePopUp>
    </>
  );
};
export default UsersTable;
